import React from 'react';
import { Global, css } from '@emotion/react';
import useEventHandler from 'Hooks/useEventHandler';

const GlobalCursorStyles = ({ isResizing }: { isResizing: boolean }) => {
  return isResizing ? (
    <Global
      styles={css`
        body * {
          cursor: ns-resize !important ;
        }
      `}
    />
  ) : null;
};

export default function useResize(resizableRef: React.MutableRefObject<HTMLDivElement>) {
  const [isResizing, setIsResizing] = React.useState(false);
  const originalResizableClientY = React.useRef(0);
  const originalResizableHeight = React.useRef(0);

  const handleMouseMove = useEventHandler((e: MouseEvent) => {
    if (isResizing) {
      const height =
        originalResizableHeight.current + (e.clientY - originalResizableClientY.current);

      // eslint-disable-next-line no-param-reassign
      resizableRef.current.style.height = `${height}px`;
    }
  });

  React.useEffect(() => {
    const handleMouseUp = () => {
      setIsResizing(false);
      window.removeEventListener('mousemove', handleMouseMove);
    };

    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove]);

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      originalResizableHeight.current = resizableRef.current.getBoundingClientRect().height;
      originalResizableClientY.current = resizableRef.current.getBoundingClientRect().bottom;
      window.addEventListener('mousemove', handleMouseMove);
      setIsResizing(true);
    },
    [handleMouseMove, resizableRef],
  );

  return {
    handleMouseDown,
    GlobalCursorStyles: <GlobalCursorStyles isResizing={isResizing} />,
  };
}
